import { useMemo } from 'react';
import { List } from 'immutable';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectWaitingParticipants } from 'flow/selectors/video-selectors';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';

/** Someone asking to join, resolved for display. */
export interface WaitingParticipant {
    /** Their join name, `<uuid>-<nick>` — how the server identifies them. */
    name: string;
    /** Just the name they typed in the lobby. */
    displayName: string;
}

/**
 * The people currently asking to join (VID-76), in the order the server lists
 * them.
 *
 * They are not in the roster yet, so unlike useMeetingMembers there is no
 * contact, extension or track state to resolve — the join name is everything
 * the server knows about them, and the nickname it carries is what we show.
 * Memoised all the same, so the React.memo'd rows sit still while the panel's
 * ancestor re-renders around them.
 */
const useWaitingParticipants = (): WaitingParticipant[] => {
    const waitingParticipants: List<string> = useAppSelector(
        selectWaitingParticipants,
    );

    return useMemo(
        () =>
            waitingParticipants.toArray().map((name: string) => ({
                name,
                displayName: sliceUUIDFromUserName(name),
            })),
        [waitingParticipants],
    );
};

export default useWaitingParticipants;
